/**
 * Trucking daily trip inspections (api#855, #857) — the shapes the API returns
 * and the words this app puts on them.
 *
 * The CHECKLIST is the server's, chosen by the company's country: CA → NSC
 * Standard 13 Schedule 1, US → 49 CFR 396.11. Its item and defect wording is
 * verbatim from the regulation and arrives with `GET /checklist`; nothing here
 * restates it. What the two severities are CALLED differs by regime and also
 * arrives with the checklist (`severity_labels`), so a US report never says
 * "Major defect" and a Canadian one never says "Affects safe operation".
 */
import { getApiErrorMessage } from '../api/client';
import type { ApiEnvelope } from './api-envelope';
import type { ItemResult, UnitRole, DefectSeverity, TripInspectionType } from '../types/trip-inspections';

export type {
  DefectSeverity, ItemResult, UnitRole, ChecklistDefect, ChecklistItem, Checklist,
  TripInspectionListRow, TripInspectionDefect, TripInspectionDetail, TripInspectionType, LastPostTrip,
} from '../types/trip-inspections';

/**
 * The two kinds (api 0437), one checklist. Pre-trip is before the unit is
 * driven and reviews its last post-trip (49 CFR 396.13); post-trip is the end
 * of the shift. A value outside the two reads as an em dash, never as either.
 */
export const TRIP_TYPES: TripInspectionType[] = ['pre_trip', 'post_trip'];
export const TRIP_TYPE_LABEL: Record<TripInspectionType, string> = {
  pre_trip: 'Pre-trip',
  post_trip: 'Post-trip',
};
export const tripTypeLabel = (t: string | null | undefined): string =>
  (t != null && (TRIP_TYPES as string[]).includes(t)) ? TRIP_TYPE_LABEL[t as TripInspectionType] : '—';
export const asTripType = (t: string | null | undefined): TripInspectionType | null =>
  (t != null && (TRIP_TYPES as string[]).includes(t)) ? (t as TripInspectionType) : null;

export const ITEM_RESULT_LABEL: Record<ItemResult, string> = {
  no_defect: 'No defect',
  defect: 'Defect',
  not_present: 'Not on this unit',
};

export const UNIT_ROLE_LABEL: Record<UnitRole, string> = {
  power: 'Power unit',
  trailer: 'Trailer',
};

export const CERTIFICATION_LABEL: Record<'repaired' | 'repair_unnecessary', string> = {
  repaired: 'Repaired',
  repair_unnecessary: 'Repair unnecessary',
};

export const personName = (first: string | null | undefined, last: string | null | undefined): string =>
  [first, last].filter((x) => x != null && String(x).trim() !== '').join(' ') || '—';

/**
 * The report's outcome in one word, from the counts the list returns. A major
 * defect outranks a minor one; `no_defects` is the server's own derivation.
 */
export type ReportOutcome = 'no_defects' | 'minor' | 'major';
export function reportOutcome(r: { no_defects: boolean; major_defects?: number; minor_defects?: number; defects?: { severity: DefectSeverity }[] }): ReportOutcome {
  if (r.defects) {
    if (r.defects.some((d) => d.severity === 'major')) return 'major';
    return r.defects.length > 0 ? 'minor' : 'no_defects';
  }
  if ((r.major_defects ?? 0) > 0) return 'major';
  if ((r.minor_defects ?? 0) > 0) return 'minor';
  return r.no_defects ? 'no_defects' : 'minor';
}

/**
 * THE NOT-LINKED STATE. A field login with no `trucking_drivers` row cannot
 * file or list trip inspections, and the API answers 409 with a sentence that
 * says what to do ("Ask the office to link it"). That sentence is what the
 * driver sees — never a generic failure, which would send them to try again in
 * a dead zone for a problem only the office can fix.
 */
export const DRIVER_NOT_LINKED_FALLBACK =
  'Your login is not linked to a driver record. Ask the office to link it.';

export function isDriverNotLinked(error: unknown): boolean {
  const e = error as { response?: { status?: number; data?: { error?: unknown } } } | null;
  const msg = e?.response?.data?.error;
  return e?.response?.status === 409 && typeof msg === 'string' && /not linked to a driver record/i.test(msg);
}

/** The API's own words for a refused write, with a fallback that names the action. */
export const tripErrorMessage = (error: unknown, fallback: string) => getApiErrorMessage(error, fallback);

/** Office roles: may file a paper report for any driver, see every report, certify. */
export const TRIP_OFFICE_ROLES = ['admin', 'office_staff', 'dispatcher'] as const;
export const isTripOffice = (role: string | null | undefined) =>
  (TRIP_OFFICE_ROLES as readonly string[]).includes(role ?? '');

/**
 * The record inside a success envelope, or a thrown error when it is absent.
 * A 200 with `data: null` is not a report and not a checklist; letting it
 * through would render an empty page as though the record were empty, so it
 * fails the query instead and the screen shows a load failure.
 */
export function requireData<T>(body: ApiEnvelope<T> | undefined): T {
  const d = body?.data;
  if (d == null) throw new Error('The server answered with no data.');
  return d;
}
